/**
 * Shared types for Location Groups (SmartGroups)
 *
 * Location Groups are dynamic groups of locations based on rules.
 * Behind the scenes:
 * - Each group has a 1:1 Tag in a "Location Groups" TagGroup
 * - Locations are assigned via ResourceTag(tagId, LOCATION, locationId)
 * - Membership is materialized by calling applyRules endpoint
 */
import { z } from "zod";
import { nameSchema, uuidSchema, hexColorOptionalSchema, } from "./validation-common.js";
// ===== RULE FIELD OPERATORS =====
/**
 * Field rule operators for IS/IS_NOT matching
 */
export const LocationGroupFieldOperatorSchema = z.enum(["IS", "IS_NOT"]);
/**
 * Numeric comparison operators for revenue and percentile rules
 */
export const NumericOperatorSchema = z.enum(["LESS_THAN", "GREATER_THAN"]);
/**
 * Date rule operators for temporal matching
 */
export const LocationGroupDateOperatorSchema = z.enum(["BEFORE", "AFTER", "BETWEEN"]);
// ===== LOCATION TYPE ENUM (mirrors Prisma) =====
export const LocationTypeSchema = z.enum([
    "PHYSICAL",
    "MOBILE",
    "VIRTUAL",
    "TEMPORARY",
]);
// ===== REVENUE TIMEFRAME ENUM =====
export const RevenueTimeframeSchema = z.enum([
    "LAST_30_DAYS",
    "LAST_90_DAYS",
    "LAST_YEAR",
    "YTD",
]);
// ===== RULE TYPE SCHEMAS =====
/**
 * Base rule fields shared by all rule types
 */
const BaseRuleSchema = z.object({
    id: uuidSchema.optional(), // Optional client-side ID for tracking in UI
});
/**
 * District rule - match locations in districts
 */
export const LocationGroupDistrictRuleSchema = BaseRuleSchema.extend({
    type: z.literal("DISTRICT"),
    operator: LocationGroupFieldOperatorSchema,
    values: z.array(uuidSchema).min(1, "At least one district is required"),
});
/**
 * Store Number rule - match by store number
 */
export const StoreNumberRuleSchema = BaseRuleSchema.extend({
    type: z.literal("STORE_NUMBER"),
    operator: LocationGroupFieldOperatorSchema,
    values: z.array(z.string()).min(1, "At least one store number is required"),
});
/**
 * Location Type rule - match by location type
 */
export const LocationTypeRuleSchema = BaseRuleSchema.extend({
    type: z.literal("LOCATION_TYPE"),
    operator: LocationGroupFieldOperatorSchema,
    values: z
        .array(LocationTypeSchema)
        .min(1, "At least one location type is required"),
});
/**
 * Revenue rule - match by revenue threshold
 */
export const RevenueRuleSchema = BaseRuleSchema.extend({
    type: z.literal("REVENUE"),
    operator: NumericOperatorSchema,
    value: z.number().nonnegative("Revenue must be a non-negative number"),
    timeframe: RevenueTimeframeSchema.optional(),
});
/**
 * Revenue Percentile rule - match by revenue percentile
 */
export const RevenuePercentileRuleSchema = BaseRuleSchema.extend({
    type: z.literal("REVENUE_PERCENTILE"),
    operator: NumericOperatorSchema,
    value: z
        .number()
        .min(0, "Percentile must be at least 0")
        .max(100, "Percentile must be at most 100"),
    timeframe: RevenueTimeframeSchema.optional(),
});
/**
 * Opening Date rule - match by opening date
 */
export const OpeningDateRuleSchema = BaseRuleSchema.extend({
    type: z.literal("OPENING_DATE"),
    operator: LocationGroupDateOperatorSchema,
    // For BEFORE/AFTER: use date1 only
    // For BETWEEN: use date1 (start) and date2 (end), both inclusive
    date1: z.iso.datetime({ message: "Invalid date format for date1" }),
    date2: z.iso.datetime().optional(), // Required for BETWEEN
}).refine((data) => {
    if (data.operator === "BETWEEN" && !data.date2) {
        return false;
    }
    return true;
}, { message: "date2 is required for BETWEEN operator", path: ["date2"] });
// ============================================================================
// Response Schemas with Resolved Values
// ============================================================================
/**
 * Resolved value - contains both ID and human-readable name
 * Used in response schemas to provide displayable information to the frontend
 */
export const LocationGroupResolvedValueSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * District rule response - with resolved district names
 */
export const LocationGroupDistrictRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("DISTRICT"),
    operator: LocationGroupFieldOperatorSchema,
    values: z
        .array(LocationGroupResolvedValueSchema)
        .min(1, "At least one district is required"),
});
/**
 * Store Number rule response - values are strings, no resolution needed
 */
export const StoreNumberRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("STORE_NUMBER"),
    operator: LocationGroupFieldOperatorSchema,
    values: z.array(z.string()).min(1, "At least one store number is required"),
});
/**
 * Location Type rule response - with resolved { id, name } values for consistency
 */
export const LocationTypeRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("LOCATION_TYPE"),
    operator: LocationGroupFieldOperatorSchema,
    values: z
        .array(LocationGroupResolvedValueSchema)
        .min(1, "At least one location type is required"),
});
/**
 * Revenue rule response - no values to resolve, same as input schema
 */
export const RevenueRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("REVENUE"),
    operator: NumericOperatorSchema,
    value: z.number().nonnegative("Revenue must be a non-negative number"),
    timeframe: RevenueTimeframeSchema.optional(),
});
/**
 * Revenue Percentile rule response - no values to resolve, same as input schema
 */
export const RevenuePercentileRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("REVENUE_PERCENTILE"),
    operator: NumericOperatorSchema,
    value: z
        .number()
        .min(0, "Percentile must be at least 0")
        .max(100, "Percentile must be at most 100"),
    timeframe: RevenueTimeframeSchema.optional(),
});
/**
 * Opening Date rule response - no values to resolve, same as input schema
 */
export const OpeningDateRuleResponseSchema = BaseRuleSchema.extend({
    type: z.literal("OPENING_DATE"),
    operator: LocationGroupDateOperatorSchema,
    date1: z.iso.datetime({ message: "Invalid date format for date1" }),
    date2: z.iso.datetime().optional(),
}).refine((data) => {
    if (data.operator === "BETWEEN" && !data.date2) {
        return false;
    }
    return true;
}, { message: "date2 is required for BETWEEN operator", path: ["date2"] });
/**
 * Discriminated union of all rule response types with resolved values
 */
export const LocationGroupSmartRuleResponseSchema = z.discriminatedUnion("type", [
    LocationGroupDistrictRuleResponseSchema,
    StoreNumberRuleResponseSchema,
    LocationTypeRuleResponseSchema,
    RevenueRuleResponseSchema,
    RevenuePercentileRuleResponseSchema,
    OpeningDateRuleResponseSchema,
]);
/**
 * Array of smart group rules with resolved values
 */
export const LocationGroupSmartRulesResponseSchema = z
    .array(LocationGroupSmartRuleResponseSchema)
    .default([]);
/**
 * Discriminated union of all rule types
 */
export const LocationGroupSmartRuleSchema = z.discriminatedUnion("type", [
    LocationGroupDistrictRuleSchema,
    StoreNumberRuleSchema,
    LocationTypeRuleSchema,
    RevenueRuleSchema,
    RevenuePercentileRuleSchema,
    OpeningDateRuleSchema,
]);
/**
 * Array of rules with validation
 */
export const LocationGroupSmartRulesSchema = z.array(LocationGroupSmartRuleSchema);
// ===== LOGIC SCHEMAS =====
/**
 * Group type: SMART (rule-based) or MANUAL (explicit member assignment)
 */
export const LocationGroupTypeSchema = z.enum(["SMART", "MANUAL"]);
/**
 * How rules combine (all rules must match vs any rule must match)
 * Default: AND (all rules must match)
 */
export const LocationGroupRuleLogicSchema = z.enum(["AND", "OR"]);
/**
 * How values within a rule combine
 * OR: any selected value matches (e.g., District IS North OR South)
 * AND: all selected values must match (e.g., has Type A AND Type B - rare for locations)
 * Default: OR
 */
export const LocationGroupValueLogicSchema = z.enum(["AND", "OR"]);
// ===== INPUT SCHEMAS =====
/**
 * Schema for creating a SMART location group (rule-based)
 */
export const CreateSmartLocationGroupInputSchema = z.object({
    groupType: z.literal("SMART"),
    name: nameSchema,
    color: hexColorOptionalSchema,
    rules: LocationGroupSmartRulesSchema.min(1, "At least one rule is required"),
    ruleLogic: LocationGroupRuleLogicSchema.default("AND"),
    valueLogic: LocationGroupValueLogicSchema.default("OR"),
});
/**
 * Schema for creating a MANUAL location group (explicit member assignment)
 */
export const CreateManualLocationGroupInputSchema = z.object({
    groupType: z.literal("MANUAL"),
    name: nameSchema,
    color: hexColorOptionalSchema,
});
/**
 * Schema for creating a location group (discriminated union)
 */
export const CreateLocationGroupInputSchema = z.discriminatedUnion("groupType", [
    CreateSmartLocationGroupInputSchema,
    CreateManualLocationGroupInputSchema,
]);
/**
 * Schema for updating a SMART location group
 */
export const UpdateSmartLocationGroupInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
    rules: LocationGroupSmartRulesSchema.min(1, "At least one rule is required").optional(),
    ruleLogic: LocationGroupRuleLogicSchema.optional(),
    valueLogic: LocationGroupValueLogicSchema.optional(),
});
/**
 * Schema for updating a MANUAL location group
 */
export const UpdateManualLocationGroupInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
});
/**
 * Schema for updating a location group
 * Note: groupType cannot be changed after creation
 */
export const UpdateLocationGroupInputSchema = z.object({
    name: nameSchema.optional(),
    color: hexColorOptionalSchema,
    rules: LocationGroupSmartRulesSchema.min(1, "At least one rule is required").optional(),
    ruleLogic: LocationGroupRuleLogicSchema.optional(),
    valueLogic: LocationGroupValueLogicSchema.optional(),
});
// ===== MEMBER MANAGEMENT SCHEMAS (Manual Groups) =====
/**
 * Schema for adding members to a manual group
 */
export const AddLocationGroupMembersInputSchema = z.object({
    locationIds: z.array(uuidSchema).min(1, "At least one location ID is required"),
});
/**
 * Schema for removing a member from a manual group
 */
export const RemoveLocationGroupMemberInputSchema = z.object({
    locationId: uuidSchema,
});
// ===== RESPONSE SCHEMAS =====
/**
 * Location group member response schema
 */
export const LocationGroupMemberResponseSchema = z.object({
    locationId: z.uuid(),
    name: z.string().nullable(),
    businessName: z.string().nullable(),
    storeNumber: z.string().nullable(),
    locationType: z.string().nullable(),
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    districtId: z.uuid().nullable(),
    districtName: z.string().nullable(),
});
/**
 * Compact location group response (for lists)
 */
export const LocationGroupResponseSchema = z.object({
    id: z.uuid(),
    groupType: LocationGroupTypeSchema,
    name: z.string(),
    color: z.string().nullable(),
    companyId: z.uuid(),
    tagId: z.uuid(),
    rules: z.array(LocationGroupSmartRuleResponseSchema),
    ruleLogic: LocationGroupRuleLogicSchema,
    valueLogic: LocationGroupValueLogicSchema,
    memberCount: z.number().int().nonnegative(),
    lastAppliedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Full location group response with members
 */
export const LocationGroupDetailResponseSchema = LocationGroupResponseSchema.extend({
    members: z.array(LocationGroupMemberResponseSchema),
});
/**
 * List location groups response
 */
export const ListLocationGroupsResponseSchema = z.object({
    groups: z.array(LocationGroupResponseSchema),
    total: z.number().int().nonnegative(),
});
/**
 * Apply rules response (for backward compatibility with existing contract)
 */
export const LocationGroupApplyRulesResponseSchema = z.object({
    memberCount: z.number().int().nonnegative(),
    addedCount: z.number().int().nonnegative(),
    removedCount: z.number().int().nonnegative(),
});
/**
 * Add members response (for manual groups)
 */
export const LocationGroupAddMembersResponseSchema = z.object({
    addedCount: z.number().int().nonnegative(),
    memberCount: z.number().int().nonnegative(),
});
/**
 * Remove member response (for manual groups)
 */
export const LocationGroupRemoveMemberResponseSchema = z.object({
    removed: z.boolean(),
    memberCount: z.number().int().nonnegative(),
});
/**
 * Apply rules result response
 */
export const LocationGroupApplyRulesResultSchema = z.object({
    group: LocationGroupDetailResponseSchema,
    added: z.number().int().nonnegative(),
    removed: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
});
// ===== PARAMETER SCHEMAS =====
export const LocationGroupIdParamSchema = z.object({
    groupId: uuidSchema,
});
// ===== AVAILABLE RULE FIELDS =====
/**
 * Single option for a rule field dropdown
 */
export const LocationGroupFieldOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * Available fields and values for rule configuration UI
 */
export const LocationGroupAvailableRuleFieldsResponseSchema = z.object({
    districts: z.array(LocationGroupFieldOptionSchema),
    storeNumbers: z.array(LocationGroupFieldOptionSchema), // Store number + address for display
    locationTypes: z.array(LocationGroupFieldOptionSchema),
    timeframes: z.array(LocationGroupFieldOptionSchema),
});
